import { Box, Text } from '@elkaso-app/web-design';

export interface AttributeChipsProps {
  label: string;
  options: (string | null)[];
  /** undefined = facet not filtering ("All"). null = the facet's explicit "none" value. */
  selected: string | null | undefined;
  onSelect: (value: string | null | undefined) => void;
  nullLabel?: string;
}

const Chip = ({ isSelected, text, onClick }: { isSelected: boolean; text: string; onClick: () => void }) => (
  <Box
    as='button'
    px='14px'
    py='6px'
    borderRadius='full'
    border='1.5px solid'
    borderColor={isSelected ? 'red.500' : 'gray.200'}
    bg={isSelected ? 'red.50' : 'gray.50'}
    onClick={onClick}>
    <Text fontSize='13px' fontWeight={isSelected ? 'bold' : 'medium'} color={isSelected ? 'red.600' : 'gray.700'}>
      {text}
    </Text>
  </Box>
);

// One chip row per narrowing facet, with a leading "All" chip that widens the
// facet back out. Hidden entirely when there is nothing to choose (fewer than
// two options) — chips are for narrowing, not display.
export const AttributeChips = ({ label, options, selected, onSelect, nullLabel = 'None' }: AttributeChipsProps) => {
  if (options.length < 2) return null;

  return (
    <Box mb='12px'>
      <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.1em' color='gray.500' mb='6px'>
        {label}
      </Text>
      <Box display='flex' flexWrap='wrap' style={{ gap: '8px' }}>
        <Chip isSelected={selected === undefined} text='All' onClick={() => onSelect(undefined)} />
        {options.map((option) => (
          <Chip
            key={option ?? '__none__'}
            isSelected={option === selected}
            text={option ?? nullLabel}
            onClick={() => onSelect(option)}
          />
        ))}
      </Box>
    </Box>
  );
};
